import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldAlert, Lightbulb, Loader2, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import { ideasAPI } from '@/lib/api';
import { IDEA_CATEGORIES, IDEA_IMPACTS, IDEA_EASES } from '@/lib/ideaConstants';
import { cn } from '@/lib/utils';

const EMPTY = { title: '', category: '', description: '', benefits: '', impact_estimate: '', implementation_ease: '' };

export const CreateIdeaDialog = ({ open, onOpenChange, onCreated }) => {
    const [step, setStep] = useState('guard'); // guard | form
    const [understood, setUnderstood] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const reset = () => { setStep('guard'); setUnderstood(false); setForm(EMPTY); };
    const close = (v) => { if (!v) reset(); onOpenChange(v); };
    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.title.trim() || !form.description.trim()) { toast.error('Le titre et la description sont obligatoires.'); return; }
        if (!form.category) { toast.error('Choisissez une catégorie.'); return; }
        if (!form.impact_estimate) { toast.error("Indiquez l'impact estimé."); return; }
        if (!form.implementation_ease) { toast.error('Indiquez la facilité de mise en œuvre.'); return; }
        setBusy(true);
        try {
            const { data } = await ideasAPI.create(form);
            toast.success('Idée envoyée 💡 Merci pour votre contribution !');
            onCreated && onCreated(data);
            close(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec de l'envoi de l'idée.");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="create-idea-dialog">
                <DialogTitle className="sr-only">Déposer une idée</DialogTitle>
                {step === 'guard' ? (
                    <div data-testid="idea-guard">
                        <div className="bg-[#0B1F3B] px-6 py-6 text-white">
                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-400/20 ring-1 ring-amber-300/40">
                                <ShieldAlert className="h-6 w-6 text-amber-300" />
                            </span>
                            <h2 className="mt-4 text-xl font-black">Avant de déposer votre idée</h2>
                        </div>
                        <div className="space-y-3 px-6 py-5 text-sm text-slate-600">
                            <p>Cette boîte à idées est destinée à proposer des <span className="font-semibold text-slate-900">améliorations pour Valnexo</span>.</p>
                            <p>Elle n'est <span className="font-semibold text-red-600">pas</span> destinée aux plaintes, conflits, problèmes individuels ou remontées opérationnelles.</p>
                            <p>Pour toute difficulté ou réclamation, veuillez contacter votre <span className="font-semibold">responsable secteur</span> selon le processus habituel.</p>
                            <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500">Les demandes concernant les <span className="font-medium">bugs CRM</span> ou les <span className="font-medium">nouvelles fonctionnalités CRM</span> doivent continuer à être transmises via le <span className="font-medium">support CRM dédié</span>.</p>
                        </div>
                        <div className="border-t border-slate-100 px-6 py-4">
                            <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700">
                                <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)}
                                    className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-slate-300 text-amber-600 focus:ring-amber-500" data-testid="idea-guard-checkbox" />
                                <span>J'ai compris : il s'agit bien d'une proposition d'amélioration pour Valnexo.</span>
                            </label>
                            <button onClick={() => setStep('form')} disabled={!understood} data-testid="idea-guard-continue"
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-500 py-3 text-sm font-bold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50">
                                Continuer <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div data-testid="idea-form" className="flex max-h-[85vh] flex-col">
                        <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
                            <Lightbulb className="h-5 w-5 text-amber-500" />
                            <h2 className="text-lg font-bold text-slate-900">Déposer une idée</h2>
                        </div>
                        <div className="space-y-4 overflow-y-auto px-6 py-5">
                            <Field label="Titre de l'idée" required>
                                <Input value={form.title} onChange={(e) => upd('title', e.target.value)} placeholder="Une idée en quelques mots…" maxLength={200} data-testid="idea-title-input" />
                            </Field>
                            <Field label="Catégorie" required>
                                <Select value={form.category} onValueChange={(v) => upd('category', v)}>
                                    <SelectTrigger data-testid="idea-category-select"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                                    <SelectContent>
                                        {IDEA_CATEGORIES.map((c) => (
                                            <SelectItem key={c.value} value={c.value} data-testid={`idea-category-${c.value}`}>{c.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {form.category && <p className="mt-1 text-xs text-slate-400">{IDEA_CATEGORIES.find((c) => c.value === form.category)?.desc}</p>}
                            </Field>
                            <Field label="Description détaillée" required>
                                <Textarea value={form.description} onChange={(e) => upd('description', e.target.value)} rows={4} placeholder="Décrivez votre idée concrètement…" data-testid="idea-description-input" />
                            </Field>
                            <Field label="Bénéfices attendus" hint="En quoi cette idée améliorerait-elle Valnexo ?">
                                <Textarea value={form.benefits} onChange={(e) => upd('benefits', e.target.value)} rows={3} placeholder="Gain de temps, plus de ventes, meilleure expérience…" data-testid="idea-benefits-input" />
                            </Field>
                            <Field label="Impact estimé" required>
                                <Segmented options={IDEA_IMPACTS} value={form.impact_estimate} onChange={(v) => upd('impact_estimate', v)} testid="idea-impact" />
                            </Field>
                            <Field label="Facilité de mise en œuvre" required>
                                <Segmented options={IDEA_EASES} value={form.implementation_ease} onChange={(v) => upd('implementation_ease', v)} testid="idea-ease" />
                            </Field>
                        </div>
                        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
                            <button onClick={() => setStep('guard')} className="rounded-xl px-4 py-2.5 text-sm font-medium text-slate-500 hover:bg-slate-100">Retour</button>
                            <button onClick={submit} disabled={busy} data-testid="idea-submit-btn"
                                className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-amber-600 disabled:opacity-60">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lightbulb className="h-4 w-4" />} Envoyer mon idée
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, required, hint, children }) => (
    <div>
        <label className="mb-1.5 block text-sm font-semibold text-slate-700">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        {hint && <p className="mb-1.5 text-xs text-slate-400">{hint}</p>}
        {children}
    </div>
);

const Segmented = ({ options, value, onChange, testid }) => (
    <div className="flex flex-wrap gap-2">
        {options.map((o) => (
            <button key={o.value} type="button" onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
                className={cn(
                    'inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                    value === o.value ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                )}>
                {value === o.value && <Check className="h-3.5 w-3.5" />} {o.label}
            </button>
        ))}
    </div>
);

export default CreateIdeaDialog;
